import {
  ITaxonomyTermThirdParty,
  MONITORING_PROMPT_PURPOSE_KPIS,
  MonitoringPromptKpi,
  MonitoringPromptPurposeType,
  SyncCoreDataDefinitionsEnumTranslator,
  TaxonomyFacet,
  TaxonomyTermPriority,
} from '@edgebox/sync-core-data-definitions';
import {
  ClientLocaleEntity,
  ClientTaxonomyEntity,
  ClientTaxonomyTermDraft,
  ClientTaxonomyTermEntity,
} from '@edgebox/sync-core-rest-client';
import { DetectTermColorReason, DetectTermColorResponse } from '@edgebox/sync-core-rest-client/dist/services/api/models';
import { ValueProvenance } from '@edgebox/data-definition-kit';
import React from 'react';
import { classifyEntityOwnership } from '../GovernanceAnalyticsAiAnswers.utils';
import { carriesBrandIdentity, requiresDomainForFacet } from '../GovernanceTaxonomyManagement.utils';
import { HEX_COLOR_PATTERN } from './governanceThemeColors';
import { EntityDomainSuggestion } from './useEntityDomains';
import { localeDisplayName } from './localeDisplay.utils';
import { BP_PRIMARY_BUTTON_CLASSES } from './brandPresenceClasses';

export type TaxonomyTermFormMode = 'create' | 'edit';

export interface ITaxonomyTermFormValues {
  name: string;
  description?: string;
  /** Other names the tag is matched by; each entry is trimmed and at least two characters long. */
  aliases?: string[];
  priority?: TaxonomyTermPriority;
  sourceLocaleId?: string;
  domain?: string;
  /**
   * Brand-identity facets only: the tag's brand colour, held as the text the operator typed. An
   * empty value means "no colour" — the write path turns that into an explicit clear when the tag
   * had one. Provenance is never held here: the form reads the marker the service stamped and never
   * sends one back.
   */
  color?: string;
  /**
   * Prompt-purpose facet only: what the tag's prompts are for. The server requires it there — the
   * type is what decides which KPIs are measurements rather than fixed by the question.
   */
  purposeType?: MonitoringPromptPurposeType;
  /**
   * Prompt-purpose facet only. While the form is open this holds the KPIs ticked for `custom`. On
   * submit it is that list for a `custom` type and `null` for every other type: those derive their
   * KPIs, and the server refuses — rather than drops — a derived type that still carries a list from
   * the tag's custom days, so the client has to say "no list" explicitly. Leaving the field out
   * would keep the old one.
   */
  purposeKpis?: MonitoringPromptKpi[] | null;
}

/**
 * The two purpose fields as the server takes them.
 *
 * `null` is a deliberate value (see `ITaxonomyTermFormValues.purposeKpis`); the runtime entity types
 * spell the fields without it, so the records that go to the server are written through this shape.
 */
export interface ITaxonomyTermPurposeFields {
  purposeType?: MonitoringPromptPurposeType | null;
  purposeKpis?: MonitoringPromptKpi[] | null;
}

/** Copies the purpose fields of a submitted form onto the record that goes to the server. */
export function applyPurposeFields(target: ITaxonomyTermPurposeFields, values: ITaxonomyTermFormValues): void {
  target.purposeType = values.purposeType;
  target.purposeKpis = values.purposeKpis;
}

/**
 * The record a submitted form is written onto, whether it is being created or updated.
 *
 * The client draft does not declare the purpose and third-party fields yet, though the DTO behind
 * it does; the intersection writes them the way the entity carries them.
 */
export type TaxonomyTermRecord = (ClientTaxonomyTermDraft | ClientTaxonomyTermEntity) &
  ITaxonomyTermPurposeFields & {
    thirdParty?: ITaxonomyTermThirdParty;
    color?: string | null;
    colorProvenance?: ValueProvenance | null;
  };

/**
 * Applies everything the form collects onto the record that goes to the server.
 *
 * Create and edit both write through here, so a field added to the form is applied once, in this
 * function, and reaches both submit paths.
 */
export function applyTermFormValues(target: TaxonomyTermRecord, values: ITaxonomyTermFormValues, facet?: TaxonomyFacet): void {
  target.name = values.name;
  target.description = values.description;
  target.priority = values.priority;

  if (isPurposeFacet(facet)) {
    applyPurposeFields(target, values);
  }

  // The two provenance markers are the service's to stamp from who wrote a value and what changed;
  // this form only ever displays them. An edit clones the whole entity, so they arrive on `target`
  // — remove them so no write carries a provenance, which is what keeps every unchanged value
  // eligible for automatic detection and the console backfill.
  delete target.colorProvenance;

  if (requiresDomainForFacet(facet)) {
    if (!values.domain) {
      throw new Error('Domain is required for this tag.');
    }

    // An authority source stands for one or more hosts, so it keeps the `domains` list.
    target.thirdParty = {
      domain: values.domain,
      domains: [values.domain],
    };
  } else if (carriesBrandIdentity(facet)) {
    // A brand-identity tag carries a single main domain and never a `domains` list, which the
    // service refuses on these facets. An empty value clears the domain.
    const domain = (values.domain || '').trim();
    target.thirdParty = domain ? { domain } : undefined;
  } else if (target.thirdParty) {
    // Every other facet keeps whatever domain it already carried, minus the provenance marker.
    const nextThirdParty: ITaxonomyTermThirdParty = { ...target.thirdParty };
    delete nextThirdParty.domainProvenance;
    target.thirdParty = nextThirdParty;
  }

  if (carriesBrandIdentity(facet)) {
    applyColorValue(target, values);
  }
}

/**
 * Writes the brand colour onto the record as a value only.
 *
 * A typed colour is sent trimmed and lower-cased. An emptied field clears a colour the tag had —
 * sent as `null`, the service's record of "there is no colour", never as an empty string, which it
 * refuses — and does nothing when there was none to clear. A colour left untouched is re-sent as it
 * loaded, so the service sees no change and keeps the provenance it already holds.
 */
function applyColorValue(target: TaxonomyTermRecord, values: ITaxonomyTermFormValues): void {
  const priorColor = typeof target.color === 'string' && target.color ? target.color : undefined;
  const typedColor = (values.color || '').trim().toLowerCase();

  if (typedColor) {
    target.color = typedColor;
  } else if (priorColor) {
    target.color = null;
  } else {
    target.color = undefined;
  }
}

export function isPurposeFacet(facet?: TaxonomyFacet) {
  return facet === TaxonomyFacet.MonitoringPromptPurpose;
}

/**
 * One read-only line saying where a stored colour or domain came from, or nothing when the service
 * holds no provenance for it. The provenance value is rendered through the package translator, not a
 * local label map; the form displays it and never offers a way to edit it.
 */
function provenanceNote(subject: 'colour' | 'domain', provenance: ValueProvenance | null | undefined): string | undefined {
  if (!provenance) {
    return undefined;
  }
  const label = SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue('ValueProvenance', provenance);
  return `${subject === 'colour' ? 'Colour' : 'Domain'} source: ${label}`;
}

/** One line per type on the shape of prompt it stands for, so the operator can pick without a manual. */
const PURPOSE_TYPE_SHAPE: Readonly<Record<MonitoringPromptPurposeType, string>> = {
  [MonitoringPromptPurposeType.Discovery]: 'Neither your brand nor a rival is named — the answer has to find you.',
  [MonitoringPromptPurposeType.NeedDiscovery]:
    'Asks what matters when choosing — no brand or option is named, so only the attributes it surfaces are read.',
  [MonitoringPromptPurposeType.SolutionDiscovery]:
    'Asks which options exist and expects them by name — neither your brand nor a rival is named.',
  [MonitoringPromptPurposeType.Comparison]: 'Your brand and at least one rival are named — the answer weighs you against them.',
  [MonitoringPromptPurposeType.BrandProbe]: 'Your brand is named and no rival is — the answer is about you.',
  [MonitoringPromptPurposeType.CompetitorProbe]: 'A rival is named and your brand is not — the answer has to bring you up.',
  [MonitoringPromptPurposeType.Custom]: 'No fixed shape — tick the KPIs this purpose shows.',
};

function kpiLabel(kpi: MonitoringPromptKpi) {
  return SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue('MonitoringPromptKpi', kpi);
}

interface ITaxonomyTermFormProps {
  mode: TaxonomyTermFormMode;
  taxonomy: ClientTaxonomyEntity;
  term?: ClientTaxonomyTermEntity;
  locales: ClientLocaleEntity[];
  /**
   * What the screen that sent the operator here already knows, in create mode.
   *
   * The form still opens for review — nothing is saved until they submit — so this only spares
   * them retyping a hostname they just clicked.
   */
  createPrefill?: Partial<ITaxonomyTermFormValues>;
  initialSourceLocaleId?: string;
  localeRequired: boolean;
  loading?: boolean;
  saving?: boolean;
  error?: string;
  onCancel: () => void;
  onSubmit: (values: ITaxonomyTermFormValues) => Promise<void>;
  /**
   * Re-reads the brand colour from the tag's favicon and returns the outcome the route answers.
   *
   * Wired only where Edit is available on a brand-identity facet; absent everywhere else, which is
   * what hides the action. The stored colour is already in place at the same id when this resolves,
   * so the form writes the returned value straight into its own state.
   */
  onRedetectColor?: () => Promise<DetectTermColorResponse>;
  /** An inferred domain the service offers for this tag, shown beside an empty Domain field. */
  domainSuggestion?: EntityDomainSuggestion;
}

/** What the form says for each terminal outcome of a re-detection that stored no colour. */
export const DETECT_REASON_MESSAGE: Record<DetectTermColorReason, string> = {
  [DetectTermColorReason.NoDomain]: 'Add a main domain first — the colour is read from that site’s icon.',
  [DetectTermColorReason.InvalidDomain]: 'This domain could not be read as a website address.',
  [DetectTermColorReason.FaviconUnavailable]: 'No icon could be fetched for this domain.',
  [DetectTermColorReason.UnsupportedImage]: 'This site’s icon is in a format we cannot read.',
  [DetectTermColorReason.NoQualifyingColor]: 'No brand colour could be read from this favicon.',
  [DetectTermColorReason.ManualColorKept]: 'This colour is yours, so detection left it alone.',
};

/**
 * Sync Core ignores anything shorter when it matches aliases, so an entry below this floor could be
 * saved but would never match a thing.
 */
export const ALIAS_MIN_LENGTH = 2;

/** Why `alias` may not join the list, or nothing when it may. */
function aliasRefusal(alias: string, name: string, aliases: string[]): string | undefined {
  if (alias.length < ALIAS_MIN_LENGTH) {
    return `An alias needs at least ${ALIAS_MIN_LENGTH} characters — anything shorter is never matched.`;
  }

  const lowered = alias.toLowerCase();
  if (name.trim().toLowerCase() === lowered) {
    return 'This alias is the same as the name, which is always matched anyway.';
  }

  if (aliases.some((entry) => entry.toLowerCase() === lowered)) {
    return 'This alias is already in the list.';
  }

  return undefined;
}

function initialValues(
  mode: TaxonomyTermFormMode,
  taxonomy: ClientTaxonomyEntity,
  locales: ClientLocaleEntity[],
  initialSourceLocaleId?: string,
  term?: ClientTaxonomyTermEntity,
  createPrefill?: Partial<ITaxonomyTermFormValues>
): ITaxonomyTermFormValues {
  if (mode === 'edit' && term) {
    return {
      name: term.name,
      description: term.description || '',
      aliases: term.aliases ? [...term.aliases] : [],
      priority: term.priority as TaxonomyTermPriority | undefined,
      sourceLocaleId: term.source?.locale?.getId?.(),
      domain: term.thirdParty?.domain || term.thirdParty?.domains?.[0] || '',
      color: term.color || '',
      // The server reports a legacy tag's type as `null`; the form treats that as "not chosen yet".
      purposeType: term.purposeType ?? undefined,
      purposeKpis: term.purposeKpis ? [...term.purposeKpis] : [],
    };
  }

  const selectedLocale = initialSourceLocaleId ? locales.find((entry) => String(entry.id) === initialSourceLocaleId) : undefined;

  return {
    // A locale term names itself after the locale that was picked, which is a stronger claim than
    // anything a linking screen could pass.
    name: selectedLocale ? localeDisplayName(selectedLocale) : createPrefill?.name || '',
    description: '',
    aliases: [],
    priority: undefined,
    sourceLocaleId: initialSourceLocaleId,
    domain: createPrefill?.domain || '',
    color: '',
    purposeType: undefined,
    purposeKpis: [],
  };
}

export function GovernanceTaxonomyTermForm({
  mode,
  taxonomy,
  term,
  locales,
  createPrefill,
  initialSourceLocaleId,
  localeRequired,
  loading,
  saving,
  error,
  onCancel,
  onSubmit,
  onRedetectColor,
  domainSuggestion,
}: ITaxonomyTermFormProps) {
  const [values, setValues] = React.useState<ITaxonomyTermFormValues>(() =>
    initialValues(mode, taxonomy, locales, initialSourceLocaleId, term, createPrefill)
  );
  const [validationError, setValidationError] = React.useState<string | undefined>(undefined);
  const [nameTouched, setNameTouched] = React.useState(mode === 'edit');
  const [aliasDraft, setAliasDraft] = React.useState('');
  const [aliasError, setAliasError] = React.useState<string | undefined>(undefined);
  const [redetecting, setRedetecting] = React.useState(false);
  // Announced in a polite live region so a screen-reader user hears the outcome without hunting.
  const [redetectMessage, setRedetectMessage] = React.useState<string | undefined>(undefined);
  // Serialized so a fresh object identity on every render cannot wipe what the operator has typed.
  const prefillSignature = JSON.stringify(createPrefill ?? {});

  React.useEffect(() => {
    setValues(initialValues(mode, taxonomy, locales, initialSourceLocaleId, term, createPrefill));
    setValidationError(undefined);
    setNameTouched(mode === 'edit');
    setAliasDraft('');
    setAliasError(undefined);
    setRedetectMessage(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSourceLocaleId, locales, mode, taxonomy, term, prefillSignature]);

  const facet = taxonomy.facet as TaxonomyFacet;
  const isPurpose = isPurposeFacet(facet);
  const carriesBrand = carriesBrandIdentity(facet);
  const domainRequired = requiresDomainForFacet(facet);
  // The Domain input shows wherever a facet may carry one; the Colour input only for brand identity.
  const showDomainInput = domainRequired || carriesBrand;
  // The customer's own tags keep the theme colour on every surface, so a colour saved here is stored
  // but never painted; the form says so rather than letting the operator expect a change.
  const isOwnSideBrand = carriesBrand && classifyEntityOwnership(facet) === 'own';
  // A tag from before purpose types existed: the form will not save it without one.
  const legacyUntyped = isPurpose && mode === 'edit' && !term?.purposeType;

  const trimmedColorValue = (values.color || '').trim().toLowerCase();
  const swatchColor = HEX_COLOR_PATTERN.test(trimmedColorValue) ? trimmedColorValue : undefined;
  const colorProvenanceNote = provenanceNote('colour', term?.colorProvenance);
  const domainProvenanceNote = provenanceNote('domain', term?.thirdParty?.domainProvenance);

  const onColorChange = (nextColor: string) => {
    setValidationError(undefined);
    setValues((current) => ({ ...current, color: nextColor }));
  };

  const clearColor = () => {
    setValidationError(undefined);
    setValues((current) => ({ ...current, color: '' }));
  };

  // The suggested domain writes nothing until it is accepted; accepting fills the field like any edit.
  const acceptDomainSuggestion = (domain: string) => {
    setValues((current) => ({ ...current, domain }));
  };

  const redetectColor = async () => {
    if (!onRedetectColor || redetecting) {
      return;
    }

    setRedetecting(true);
    setRedetectMessage(undefined);
    try {
      const outcome = await onRedetectColor();
      const detectedColor = (outcome.color || '').trim().toLowerCase();
      if (outcome.detected && detectedColor) {
        // The colour is already stored server-side at the same id; write it into the form so the
        // next save keeps it rather than reverting to the value that loaded.
        setValues((current) => ({ ...current, color: detectedColor }));
        setValidationError(undefined);
        setRedetectMessage(`Found ${detectedColor}. Save to keep it.`);
      } else {
        // A reason from a service one release ahead is not in the map; the live region still says
        // something rather than falling silent for a screen reader.
        setRedetectMessage((outcome.reason && DETECT_REASON_MESSAGE[outcome.reason]) || 'No brand colour could be read from this favicon.');
      }
    } catch {
      // A refusal or a missing endpoint is a failure of the action, not "no colour found".
      setRedetectMessage('The colour could not be re-detected. Please try again.');
    } finally {
      setRedetecting(false);
    }
  };

  const onNameChange = (nextName: string) => {
    setNameTouched(true);
    setValues((current) => ({
      ...current,
      name: nextName,
    }));
  };

  const addAlias = (raw: string): void => {
    const alias = raw.trim();
    if (!alias) {
      setAliasDraft('');
      return;
    }

    const refusal = aliasRefusal(alias, values.name, values.aliases || []);
    if (refusal) {
      setAliasError(refusal);
      return;
    }

    setAliasError(undefined);
    setAliasDraft('');
    setValues((current) => ({ ...current, aliases: [...(current.aliases || []), alias] }));
  };

  const onAliasKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ',') {
      // Enter adds the alias rather than submitting the form it is typed into.
      event.preventDefault();
      addAlias(aliasDraft);
    }
  };

  const removeAlias = (alias: string) => {
    setAliasError(undefined);
    setValues((current) => ({ ...current, aliases: (current.aliases || []).filter((entry) => entry !== alias) }));
  };

  const onPurposeTypeChange = (value: string) => {
    const nextType = Object.values(MonitoringPromptPurposeType).find((type) => type === value);
    // The ticked KPIs stay in state across a switch away from Custom and back, so a slip of the
    // select does not cost the operator their selection; what is SENT is decided at submit.
    setValues((current) => ({ ...current, purposeType: nextType }));
  };

  const togglePurposeKpi = (kpi: MonitoringPromptKpi) => {
    setValues((current) => {
      const selected = current.purposeKpis || [];
      return {
        ...current,
        // Catalogue order whatever the order of ticking, so two tags holding the same KPIs read the same.
        purposeKpis: Object.values(MonitoringPromptKpi).filter((entry) =>
          entry === kpi ? !selected.includes(kpi) : selected.includes(entry)
        ),
      };
    });
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();

    const trimmedName = values.name.trim();
    const trimmedDomain = (values.domain || '').trim();
    const trimmedColor = (values.color || '').trim().toLowerCase();

    if (!trimmedName) {
      setValidationError('Name is required.');
      return;
    }

    if (domainRequired && !trimmedDomain) {
      setValidationError('Domain is required for this tag.');
      return;
    }

    if (carriesBrand && trimmedColor && !HEX_COLOR_PATTERN.test(trimmedColor)) {
      setValidationError('A brand colour must be a six-digit hex value such as #1a73e8.');
      return;
    }

    if (localeRequired && !values.sourceLocaleId) {
      setValidationError('A source locale is required for locale taxonomy terms.');
      return;
    }

    if (isPurpose && !values.purposeType) {
      setValidationError('Choose a purpose type — it decides which KPIs this purpose shows.');
      return;
    }

    const isCustomPurpose = isPurpose && values.purposeType === MonitoringPromptPurposeType.Custom;
    if (isCustomPurpose && !(values.purposeKpis || []).length) {
      setValidationError('Tick at least one KPI for a custom purpose.');
      return;
    }

    // An alias typed without being committed to the list is still what the operator meant to save:
    // it joins the list here, and when it cannot, the submit stops on the inline message instead of
    // dropping it.
    const pendingAlias = aliasDraft.trim();
    let aliases = values.aliases || [];
    if (pendingAlias) {
      const refusal = aliasRefusal(pendingAlias, trimmedName, aliases);
      if (refusal) {
        setAliasError(refusal);
        return;
      }

      aliases = [...aliases, pendingAlias];
      setAliasDraft('');
      setValues((current) => ({ ...current, aliases }));
    }

    setValidationError(undefined);

    await onSubmit({
      ...values,
      name: trimmedName,
      description: (values.description || '').trim() || undefined,
      aliases,
      domain: trimmedDomain || undefined,
      // A colour only travels on a brand-identity facet; the write path turns an empty value into a
      // clear when the tag had one, and leaves the field absent otherwise.
      color: carriesBrand ? trimmedColor || undefined : undefined,
      ...(isPurpose
        ? {
            purposeType: values.purposeType,
            // A derived type never carries a list; `null` makes the server drop one the tag may still hold.
            purposeKpis: isCustomPurpose ? values.purposeKpis || [] : null,
          }
        : { purposeType: undefined, purposeKpis: undefined }),
    });
  };

  if (loading) {
    return <div className="text-sm text-slate-500">Loading term...</div>;
  }

  const selectedSourceLocale = values.sourceLocaleId ? locales.find((entry) => String(entry.id) === values.sourceLocaleId) : undefined;
  const derivedPurposeKpis =
    values.purposeType && values.purposeType !== MonitoringPromptPurposeType.Custom
      ? MONITORING_PROMPT_PURPOSE_KPIS[values.purposeType]
      : undefined;

  return (
    <div className="max-w-3xl rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-200 px-6 py-4">
        <h3 className="text-lg font-semibold text-slate-900">
          {mode === 'create' ? `Create ${taxonomy.name} term` : `Edit ${taxonomy.name} term`}
        </h3>
        <p className="mt-1 text-sm text-slate-500">Define the taxonomy term details and save your changes.</p>
      </div>

      <form onSubmit={(event) => void submit(event)} className="space-y-5 px-6 py-5">
        {error ? <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div> : null}
        {validationError ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">{validationError}</div>
        ) : null}

        <div>
          <label className="mb-1 block text-sm font-bold text-slate-700">Name</label>
          <input
            type="text"
            value={values.name}
            onChange={(event) => onNameChange(event.target.value)}
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm transition-all duration-150 focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/10"
            placeholder={localeRequired ? 'Select source locale to prefill' : 'Enter a term name'}
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-bold text-slate-700">Description</label>
          <textarea
            value={values.description || ''}
            onChange={(event) => setValues((current) => ({ ...current, description: event.target.value }))}
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm transition-all duration-150 focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/10"
            rows={4}
            placeholder="Optional description"
          />
        </div>

        <div>
          <label htmlFor="taxonomy-term-aliases" className="mb-1 block text-sm font-bold text-slate-700">
            Aliases
          </label>
          <input
            id="taxonomy-term-aliases"
            type="text"
            value={aliasDraft}
            onChange={(event) => {
              setAliasError(undefined);
              setAliasDraft(event.target.value);
            }}
            onKeyDown={onAliasKeyDown}
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm transition-all duration-150 focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/10"
            placeholder="Type an alias and press Enter"
          />
          <p className="mt-1 text-xs text-slate-500">
            Other names this tag also goes by; press Enter or comma to add one. Avoid overly generic aliases: a single common word will
            attribute unrelated text; prefer distinctive short forms.
          </p>
          {aliasError ? <p className="mt-1 text-xs text-amber-700">{aliasError}</p> : null}
          {values.aliases?.length ? (
            <ul aria-label="Added aliases" className="mt-2 flex flex-wrap gap-1.5 list-none p-0">
              {values.aliases.map((alias) => (
                <li
                  key={alias}
                  className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs text-slate-700"
                >
                  {alias}
                  <button
                    type="button"
                    aria-label={`Remove alias ${alias}`}
                    onClick={() => removeAlias(alias)}
                    className="text-slate-400 hover:text-slate-600"
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>

        {isPurpose ? (
          <div>
            <label htmlFor="taxonomy-term-purpose-type" className="mb-1 block text-sm font-bold text-slate-700">
              Purpose type
            </label>
            {legacyUntyped ? (
              <div className="mb-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
                This purpose has no type yet. Choose one to save it — until then its tab on AI Visibility stays blocked, because nothing
                says which of its KPIs are measurements.
              </div>
            ) : null}
            <select
              id="taxonomy-term-purpose-type"
              value={values.purposeType || ''}
              onChange={(event) => onPurposeTypeChange(event.target.value)}
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm transition-all duration-150 focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/10"
            >
              <option value="">Choose what these prompts are for</option>
              {Object.values(MonitoringPromptPurposeType).map((type) => (
                <option key={type} value={type}>
                  {SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue('MonitoringPromptPurposeType', type)}
                </option>
              ))}
            </select>
            {values.purposeType ? <p className="mt-1 text-xs text-slate-500">{PURPOSE_TYPE_SHAPE[values.purposeType]}</p> : null}
            {derivedPurposeKpis ? (
              <div className="mt-2">
                <ul aria-label="KPIs this purpose shows" className="flex flex-wrap gap-1.5 list-none p-0 m-0">
                  {derivedPurposeKpis.map((kpi) => (
                    <li key={kpi} className="rounded-md border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs text-slate-700">
                      {kpiLabel(kpi)}
                    </li>
                  ))}
                </ul>
                <p className="mt-1 text-xs text-slate-500">The type fixes this set. Choose Custom to tick KPIs yourself.</p>
              </div>
            ) : null}
            {values.purposeType === MonitoringPromptPurposeType.Custom ? (
              <fieldset className="mt-2">
                <legend className="text-xs font-bold text-slate-600">KPIs to show</legend>
                <div className="mt-1 grid grid-cols-2 gap-1.5 sm:grid-cols-3">
                  {Object.values(MonitoringPromptKpi).map((kpi) => (
                    <label key={kpi} className="flex items-center gap-2 text-sm text-slate-700">
                      <input type="checkbox" checked={(values.purposeKpis || []).includes(kpi)} onChange={() => togglePurposeKpi(kpi)} />
                      {kpiLabel(kpi)}
                    </label>
                  ))}
                </div>
              </fieldset>
            ) : null}
          </div>
        ) : null}

        {localeRequired ? (
          <div>
            <label className="mb-1 block text-sm font-bold text-slate-700">Source locale</label>
            <select
              value={values.sourceLocaleId || ''}
              onChange={(event) => {
                const localeId = event.target.value || undefined;
                const selectedLocale = locales.find((entry) => String(entry.id) === localeId);

                setValues((current) => {
                  if (!selectedLocale || mode !== 'create') {
                    return { ...current, sourceLocaleId: localeId };
                  }

                  const nextName = nameTouched ? current.name : localeDisplayName(selectedLocale);

                  return {
                    ...current,
                    sourceLocaleId: localeId,
                    name: nextName,
                  };
                });
              }}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white disabled:bg-slate-50 disabled:text-slate-500"
            >
              <option value="">Select locale</option>
              {locales.map((locale) => (
                <option key={String(locale.id)} value={String(locale.id)}>
                  {localeDisplayName(locale)}
                </option>
              ))}
            </select>
            {mode === 'edit' && selectedSourceLocale ? (
              <p className="mt-1 text-xs text-slate-500">Current source locale: {localeDisplayName(selectedSourceLocale)}</p>
            ) : null}
          </div>
        ) : null}

        {showDomainInput ? (
          <div>
            <label htmlFor="taxonomy-term-domain" className="mb-1 block text-sm font-bold text-slate-700">
              Domain
            </label>
            <input
              id="taxonomy-term-domain"
              type="text"
              value={values.domain || ''}
              onChange={(event) => setValues((current) => ({ ...current, domain: event.target.value }))}
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm transition-all duration-150 focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/10"
              placeholder="example.com"
            />
            <p className="mt-1 text-xs text-slate-500">
              {domainRequired ? 'The main website this tag stands for.' : 'The main website this tag stands for — optional.'}
            </p>
            {domainProvenanceNote ? <p className="mt-1 text-xs text-slate-500">{domainProvenanceNote}</p> : null}
            {!values.domain && domainSuggestion?.source === 'inferred' ? (
              <div className="mt-2 flex items-center gap-2 text-xs text-slate-600">
                <span>Suggested{domainSuggestion.distinctRuns ? ` from ${domainSuggestion.distinctRuns} runs` : ''} — confirm to save</span>
                <button
                  type="button"
                  onClick={() => acceptDomainSuggestion(domainSuggestion.domain)}
                  className="rounded-md border border-slate-200 bg-slate-50 px-2 py-0.5 font-bold text-slate-700 hover:bg-slate-100"
                >
                  Use {domainSuggestion.domain}
                </button>
              </div>
            ) : null}
          </div>
        ) : null}

        {carriesBrand ? (
          <div>
            <label htmlFor="taxonomy-term-color" className="mb-1 block text-sm font-bold text-slate-700">
              Brand colour
            </label>
            <div className="flex items-center gap-2">
              {swatchColor ? (
                <span
                  aria-hidden="true"
                  className="h-9 w-9 shrink-0 rounded-lg border border-slate-200"
                  // A runtime colour cannot be a class; every colour surface in this tree paints it inline.
                  style={{ backgroundColor: swatchColor }}
                />
              ) : null}
              <input
                id="taxonomy-term-color"
                type="text"
                value={values.color || ''}
                onChange={(event) => onColorChange(event.target.value)}
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm transition-all duration-150 focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/10"
                placeholder="#1a73e8"
              />
              {values.color ? (
                <button
                  type="button"
                  aria-label="Clear brand colour"
                  onClick={clearColor}
                  className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50"
                >
                  Clear
                </button>
              ) : null}
            </div>
            <p className="mt-1 text-xs text-slate-500">
              {isOwnSideBrand
                ? 'Stored for reference. Your own tags always appear in your theme colour, so this does not change what is painted.'
                : 'A six-digit hex value such as #1a73e8, painted wherever this tag appears in colour.'}
            </p>
            {colorProvenanceNote ? <p className="mt-1 text-xs text-slate-500">{colorProvenanceNote}</p> : null}
            {onRedetectColor ? (
              <div className="mt-2">
                <button
                  type="button"
                  onClick={() => void redetectColor()}
                  disabled={redetecting}
                  className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                >
                  {redetecting ? 'Re-detecting…' : 'Re-detect from favicon'}
                </button>
                <p role="status" aria-live="polite" className="mt-1 text-xs text-slate-500">
                  {redetectMessage}
                </p>
              </div>
            ) : null}
          </div>
        ) : null}

        <div>
          <label className="mb-1 block text-sm font-bold text-slate-700">Priority</label>
          <select
            value={values.priority || ''}
            onChange={(event) =>
              setValues((current) => ({
                ...current,
                priority: (event.target.value || undefined) as TaxonomyTermPriority | undefined,
              }))
            }
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm transition-all duration-150 focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/10"
          >
            <option value="">Default</option>
            {Object.values(TaxonomyTermPriority).map((priority) => (
              <option key={priority} value={priority}>
                {SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue('TaxonomyTermPriority', priority)}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-slate-200 pt-4">
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-600 transition-colors duration-150 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          >
            Cancel
          </button>
          <button type="submit" disabled={!!saving} className={BP_PRIMARY_BUTTON_CLASSES}>
            {saving ? 'Saving…' : mode === 'create' ? 'Create term' : 'Save changes'}
          </button>
        </div>
      </form>
    </div>
  );
}
